import { useMemo } from "react";
import { ChartDataItem, PieChartProps } from "../types/dashboard";
import { useUser } from "src/shared/context/UserContext";

export const usePieChartData = ({ Orders = 0, amount = 0, ReceivedAmount = 0, OutstandingAmount = 0, Discount = 0, DeliveryFee = 0, ParcelFee = 0, tips = 0, timeRange = "today", businessType = ""}: PieChartProps) => {
  const { userConfigration } = useUser();
  const toBoolean = (value: any) => value === true || value === "true";
  const parcelFeeEnabled = toBoolean(userConfigration?.parcelFeeEnabled);
  const tipsEnabled = toBoolean(userConfigration?.tipsEnabled);

  // Only show parcel fee and tips for Restaurant if enabled in config
  const showParcelFee = businessType === "Restaurant" && parcelFeeEnabled;
  const showTips = businessType === "Restaurant" && tipsEnabled;

  const hasValidData = useMemo(() => {
    const values = [Orders, amount, ReceivedAmount, OutstandingAmount, Discount, DeliveryFee];

    // Only include Parcel Fee and Tips for Restaurant if enabled
    if (showParcelFee) {
      values.push(ParcelFee);
    }
    if (showTips) {
      values.push(tips);
    }

    return values.some((val) => val > 0);
  }, [Orders, amount, ReceivedAmount, OutstandingAmount, Discount, DeliveryFee, ParcelFee, tips, showParcelFee, showTips]);

  const chartData: ChartDataItem[] = useMemo(() => {
    const data: ChartDataItem[] = [
      { name: "Orders", value: Orders, color: "#3b82f6" },
      { name: "Amount", value: amount, color: "#f59e0b" },
      { name: "Discount", value: Discount, color: "#EC4899" },
      { name: "Delivery Fee", value: DeliveryFee, color: "#8b5cf6" },
    ];

    // Add Parcel Fee and Tips for Restaurant if enabled (before Received)
    if (showParcelFee) {
      data.push({ name: "Parcel Fee", value: ParcelFee, color: "#06b6d4" });
    }
    if (showTips) {
      data.push({ name: "Tips", value: tips, color: "#f472b6" });
    }

    // Add Received and Outstanding after Parcel Fee and Tips
    data.push({ name: "Received", value: ReceivedAmount, color: "#10b981" }, { name: "Outstanding", value: OutstandingAmount, color: "#ef4444" });

    return data;
  }, [Orders, amount, ReceivedAmount, OutstandingAmount, Discount, DeliveryFee, ParcelFee, tips, showParcelFee, showTips]);

  const noDataMessages = {
    today: "No orders placed today",
    week: "No orders placed this week",
    month: "No orders placed this month",
  };

  const noDataMessage = noDataMessages[timeRange as keyof typeof noDataMessages] || "No data available";

  return {
    hasValidData,
    chartData,
    noDataMessage,
  };
};
